
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BookingDetails, PAGE_SIZE } from './types';
import { BookingStatusKey } from '@/types/booking-status';
import { useRealtimeBookings } from './useRealtimeBookings';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationScoping } from '@/hooks/useOrganizationScoping';
import { useUserRoles } from '@/hooks/useUserRoles';

export const useBookingsFetch = (isAdmin: boolean) => {
  const { user } = useAuth();
  const { roles, isLoading: rolesLoading } = useUserRoles();
  const { buildOrgFilter } = useOrganizationScoping();
  const [bookings, setBookings] = useState<BookingDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('all');

  // Setup realtime subscription
  useRealtimeBookings({
    bookings,
    setBookings,
    isAdmin,
    userId: user?.id
  });

  const fetchBookings = useCallback(async (page = 1, filter = 'all', reset = false) => {
    try {
      // Don't fetch if roles are still loading
      if (rolesLoading) {
        console.log('[BOOKINGS-FETCH] Roles still loading, skipping fetch');
        return;
      }

      // Wait a bit more to ensure roles are fully resolved
      if (!roles.platformRole || roles.platformRole === 'guest') {
        console.log('[BOOKINGS-FETCH] Roles not fully resolved, skipping fetch');
        return;
      }

      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsFetchingMore(true);
      }
      setError(null);

      console.log('[BOOKINGS-FETCH] Starting fetch with params:', { 
        page, 
        filter, 
        reset, 
        isAdmin,
        roles: roles,
        rolesLoading
      });

      // Changed from !inner to regular join to avoid filtering out bookings without contacts
      let query = supabase
        .from('bookings')
        .select(`
          id,
          status,
          created_at,
          collection_datetime,
          user_id,
          org_id,
          total_parcels_count,
          collection_address:address_book!collection_address_id(
            street,
            street2,
            city,
            province,
            postal_code,
            country
          ),
          delivery_address:address_book!delivery_address_id(
            street,
            street2,
            city,
            province,
            postal_code,
            country
          ),
          booking_contacts(
            contact_type,
            is_primary,
            sort_order,
            contact_book!inner(
              name,
              phone,
              email
            )
          ),
          parcels:booking_parcels(
            id,
            parcel_type,
            description,
            weight,
            dimensions,
            value,
            quantity,
            other_type_specification,
            insurance_enabled
          ),
          tracking_url,
          fulfillment_details,
          profiles!bookings_user_id_fkey(
            name,
            phone_number,
            email
          )
        `)
        .order('created_at', { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      // For super admins, don't apply any filters (org or user)
      // For org admins, apply org scoping but not user filtering
      // For regular users, apply both org scoping and user filtering
      const shouldFilterByUser = !roles.isPlatformAdmin && !roles.isOrgAdmin;
      const shouldApplyOrgFilter = !roles.isPlatformAdmin;
      
      // Apply organization scoping only if not a super admin
      if (shouldApplyOrgFilter) {
        query = buildOrgFilter(query);
      }
      
      // Apply user filtering only for non-admin users
      if (shouldFilterByUser && user?.id) {
        query = query.eq('user_id', user.id);
      }

      console.log('[BOOKINGS-FETCH] Applied filters:', {
        platformRole: roles.platformRole,
        isAdmin: roles.isAdmin,
        isPlatformAdmin: roles.isPlatformAdmin,
        isOrgAdmin: roles.isOrgAdmin,
        shouldFilterByUser,
        shouldApplyOrgFilter,
        hasAccessToAllOrgs: roles.hasAccessToAllOrgs,
        userFilterApplied: shouldFilterByUser,
        orgFilterApplied: shouldApplyOrgFilter
      });

      // Apply status filters AFTER user filtering
      if (filter === 'all') {
        // For 'all' filter, exclude cancelled bookings
        query = query.neq('status', 'cancelled');
        console.log('[BOOKINGS-FETCH] Applying all filter (excluding cancelled)');
      } else if (filter !== 'all') {
        // For specific status filters
        query = query.eq('status', filter);
        console.log('[BOOKINGS-FETCH] Applying status filter:', filter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('[BOOKINGS-FETCH] Database error:', fetchError);
        throw fetchError;
      }

      console.log('[BOOKINGS-FETCH] Raw data received:', {
        count: data?.length || 0,
        data: data?.map(b => ({ id: b.id, status: b.status, user_id: b.user_id }))
      });

      const fetchedBookings = data || [];
      const hasMoreData = fetchedBookings.length === PAGE_SIZE;

      // Transform the data to match our types
      const transformedBookings: BookingDetails[] = fetchedBookings.map(booking => {
        // Separate collection and delivery contacts (now handles empty arrays)
        const collectionContacts = booking.booking_contacts
          ?.filter((bc: any) => bc.contact_type === 'collection')
          ?.sort((a: any, b: any) => a.sort_order - b.sort_order)
          ?.map((bc: any) => ({
            name: bc.contact_book?.name || '',
            phone: bc.contact_book?.phone || '',
            email: bc.contact_book?.email || ''
          })) || [];

        const deliveryContacts = booking.booking_contacts
          ?.filter((bc: any) => bc.contact_type === 'delivery')
          ?.sort((a: any, b: any) => a.sort_order - b.sort_order)
          ?.map((bc: any) => ({
            name: bc.contact_book?.name || '',
            phone: bc.contact_book?.phone || '',
            email: bc.contact_book?.email || ''
          })) || [];

        return {
          ...booking,
          org_id: booking.org_id,
          status: booking.status, // Keep as string - no casting needed
          collection_contacts: collectionContacts,
          delivery_contacts: deliveryContacts,
          parcels: booking.parcels?.map(parcel => ({
            ...parcel,
            dimensions: parcel.dimensions as { length: string; width: string; height: string; } | null
          })) || [],
          // Set payment fields to null since they're no longer in bookings table
          payment_url: null,
          payment_confirmed: null,
          payment_confirmed_by: null,
          payment_confirmed_at: null,
          profiles: booking.profiles || null,
          fulfillment_details: booking.fulfillment_details as any || null
        };
      });

      console.log('[BOOKINGS-FETCH] Transformed bookings:', {
        count: transformedBookings.length,
        bookings: transformedBookings.map(b => ({ id: b.id, status: b.status }))
      });

      if (reset || page === 1) {
        setBookings(transformedBookings);
      } else {
        setBookings(prev => [...prev, ...transformedBookings]);
      }

      setHasMore(hasMoreData);
      setCurrentPage(page);
    } catch (err) {
      console.error('[BOOKINGS-FETCH] Error fetching bookings:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  }, [isAdmin, roles.isAdmin, roles.isPlatformAdmin, roles.isOrgAdmin, roles.platformRole, user?.id, buildOrgFilter, rolesLoading]);

  const refreshBooking = useCallback(async (bookingId: string): Promise<BookingDetails | null> => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          status,
          created_at,
          collection_datetime,
          user_id,
          org_id,
          total_parcels_count,
          collection_address:address_book!collection_address_id(
            street,
            street2,
            city,
            province,
            postal_code,
            country
          ),
          delivery_address:address_book!delivery_address_id(
            street,
            street2,
            city,
            province,
            postal_code,
            country
          ),
          booking_contacts(
            contact_type,
            is_primary,
            sort_order,
            contact_book!inner(
              name,
              phone,
              email
            )
          ),
          parcels:booking_parcels(
            id,
            parcel_type,
            description,
            weight,
            dimensions,
            value,
            quantity,
            other_type_specification,
            insurance_enabled
          ),
          
          tracking_url,
          fulfillment_details,
          profiles!bookings_user_id_fkey(
            name,
            phone_number,
            email
          )
        `)
        .eq('id', bookingId)
        .single();

      if (error) throw error;

      if (data) {
        // Separate collection and delivery contacts
        const collectionContacts = data.booking_contacts
          ?.filter((bc: any) => bc.contact_type === 'collection')
          ?.sort((a: any, b: any) => a.sort_order - b.sort_order)
          ?.map((bc: any) => ({
            name: bc.contact_book?.name || '',
            phone: bc.contact_book?.phone || '',
            email: bc.contact_book?.email || ''
          })) || [];

        const deliveryContacts = data.booking_contacts
          ?.filter((bc: any) => bc.contact_type === 'delivery')
          ?.sort((a: any, b: any) => a.sort_order - b.sort_order)
          ?.map((bc: any) => ({
            name: bc.contact_book?.name || '',
            phone: bc.contact_book?.phone || '',
            email: bc.contact_book?.email || ''
          })) || [];

        // Transform the data to match our types
        const transformedBooking: BookingDetails = {
          ...data,
          org_id: data.org_id,
          status: data.status, // Keep as string - no casting needed
          collection_contacts: collectionContacts,
          delivery_contacts: deliveryContacts,
          parcels: data.parcels?.map(parcel => ({
            ...parcel,
            dimensions: parcel.dimensions as { length: string; width: string; height: string; } | null
          })) || [],
          // Set payment fields to null since they're no longer in bookings table
          payment_url: null,
          payment_confirmed: null,
          payment_confirmed_by: null,
          payment_confirmed_at: null,
          profiles: data.profiles || null,
          fulfillment_details: data.fulfillment_details as any || null
        };

        setBookings(prevBookings => 
          prevBookings.map(booking => 
            booking.id === bookingId ? transformedBooking : booking
          )
        );
        
        return transformedBooking;
      }
      
      return null;
    } catch (err) {
      console.error('Error refreshing booking:', err);
      return null;
    }
  }, []);

  const fetchMoreBookings = useCallback(() => {
    if (!isLoading && !isFetchingMore && hasMore) {
      fetchBookings(currentPage + 1, activeFilter);
    }
  }, [fetchBookings, currentPage, activeFilter, isLoading, isFetchingMore, hasMore]);

  const filterBookings = useCallback((filter: string) => {
    console.log('[BOOKINGS-FETCH] Filter changed to:', filter);
    setActiveFilter(filter);
    setCurrentPage(1);
    fetchBookings(1, filter, true);
  }, [fetchBookings]);

  return {
    bookings,
    setBookings,
    isLoading,
    isFetchingMore,
    error,
    hasMore,
    currentPage,
    setCurrentPage,
    fetchBookings,
    fetchMoreBookings,
    filterBookings,
    activeFilter,
    refreshBooking
  };
};
